// src/pages/Login.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
// import { FaFacebook } from "react-icons/fa";
import { Link } from "react-router-dom";
import useAuthStore from "@/stores/user/authStore";
import { useLocation } from "react-router-dom";
import api from "@/config/axiosConfig";
import waytora_logo from "@/assets/logos/internal/waytora_blue_text_logo.svg";
import { toast } from "@/hooks/use-toast";

const isBetaEnabled = import.meta.env.VITE_BETA_ENABLED === "true";

const SignUp: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    user,
    signUp,
    signInWithGoogle,
    // signInWithFacebook,
    isLoading,
    isSocialLoading,
    anonymousUser,
  } = useAuthStore();

  useEffect(() => {
    if (user && !anonymousUser) {
      navigate("/lodge");
    }
  }, [user, navigate, anonymousUser]);

  const checkBetaAccess = async (emailToCheck: string) => {
    if (!isBetaEnabled) {
      return true;
    }

    try {
      setIsCheckingAccess(true);
      const response = await api.auth.post("/beta-access", {
        email: emailToCheck,
        initial_signup: false,
      });

      const { allowed, message } = response.data;

      if (!allowed) {
        toast({
          variant: "destructive",
          title: "Beta Access Denied",
          description: message || "This email is not allowed for beta access",
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking beta access:", error);
      toast({
        variant: "destructive",
        title: "Beta Access Error",
        description: "Unable to verify beta access. Please try again later.",
      });
      return false;
    } finally {
      setIsCheckingAccess(false);
    }
  };

  useEffect(() => {
    const checkAccess = async () => {
      if (!isBetaEnabled) {
        return;
      }

      const emailParam = new URLSearchParams(location.search).get("email");
      if (!emailParam) {
        navigate("/protect");
        return;
      }
      setEmail(emailParam);
      const hasAccess = await checkBetaAccess(emailParam);
      if (!hasAccess) {
        navigate("/protect");
      }
      setIsCheckingAccess(false);
    };

    checkAccess();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset error states
    setFirstNameError(false);
    setLastNameError(false);
    setEmailError(false);
    setPasswordError(false);

    // Validate first name
    if (firstName.trim() === "") {
      setFirstNameError(true);
      toast({
        variant: "destructive",
        title: "Missing First Name",
        description: "Please enter your first name",
      });
      return;
    }

    // Validate last name
    if (lastName.trim() === "") {
      setLastNameError(true);
      toast({
        variant: "destructive",
        title: "Missing Last Name",
        description: "Please enter your last name",
      });
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError(true);
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: "Please enter a valid email address",
      });
      return;
    }

    // Validate password
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,}$/;
    if (!passwordRegex.test(password)) {
      setPasswordError(true);
      toast({
        variant: "destructive",
        title: "Invalid Password",
        description:
          "Password must be at least 8 characters long, contain at least one number, one special character, and one capital letter",
      });
      return;
    }

    // Check beta access before proceeding with signup
    const hasAccess = await checkBetaAccess(email);
    if (!hasAccess) {
      return;
    }

    try {
      const result = await signUp(firstName, lastName, email, password);
      if (result) {
        navigate("/auth/callback");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error("Error signing in with Google", error);
    }
  };

  // const handleFacebookSignIn = async () => {
  //   try {
  //     await signInWithFacebook();
  //   } catch (error) {
  //     console.error("Error signing in with Facebook", error);
  //   }
  // };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <section className="bg-white overflow-hidden flex flex-col min-h-dvh justify-center relative">
      {/* Overlay for social loading blur effect */}
      <div
        className={`
          absolute inset-0 z-50 
          bg-white/30 
          transition-opacity duration-[1500ms] ease-in-out
          ${
            isSocialLoading
              ? "opacity-100 backdrop-blur-xl pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }
        `}
      />
      <div className="flex flex-row items-center">
        <div className="w-full md:w-1/2 px-4">
          <div className="container px-4 mx-auto">
            <div className="flex flex-wrap">
              <div className="w-full">
                <div className="md:max-w-lg mx-auto pt-16 md:pb-32">
                  <div className="mb-6">
                    <img
                      src={waytora_logo}
                      alt="Waytora Logo"
                      className="w-[40%]"
                    />
                  </div>
                  <h2 className="text-neutral-800 text-[75px] tracking-tight font-bold leading-[70px]">
                    Create an account and start your journey.
                  </h2>
                  <span className="px-2 transform translate-y-4 text-[75px]"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/2">
          <div className="first-letter:p-4 flex flex-col justify-center h-full mt-16">
            <div className="md:max-w-xl px-16 text-[15px]">
              <form className="relative w-full" onSubmit={handleSubmit}>
                <div className="w-full flex space-x-4 justify-between items-center mb-4">
                  <label className="w-full">
                    <p className="mb-2 text-neutral-900 font-semibold leading-normal">
                      First Name
                    </p>
                    <input
                      className={`px-4 py-3.5 w-full text-neutral-700 font-normal placeholder-neutral-400 bg-white outline-none border ${
                        firstNameError
                          ? "border-red-500 ring-red-500"
                          : "border-neutral-300"
                      } rounded-lg focus:ring-2 focus:outline-none focus:border-transparent focus:ring-indigo-600`}
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </label>
                  <label className="w-full">
                    <p className="mb-2 text-neutral-900 font-semibold leading-normal">
                      Last Name
                    </p>
                    <input
                      className={`px-4 py-3.5 w-full text-neutral-700 font-normal placeholder-neutral-400 bg-white outline-none border ${
                        lastNameError
                          ? "border-red-500 ring-red-500"
                          : "border-neutral-300"
                      } rounded-lg focus:ring-2 focus:outline-none focus:border-transparent focus:ring-indigo-600`}
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </label>
                </div>
                <label className="block mb-4">
                  <p className="mb-2 text-neutral-900 font-semibold leading-normal">
                    Email Address
                  </p>
                  <input
                    className={`px-4 py-3.5 w-full text-neutral-700 font-normal placeholder-neutral-400 bg-white outline-none border ${
                      emailError
                        ? "border-red-500 ring-red-500"
                        : "border-neutral-300"
                    } rounded-lg focus:ring-2 focus:outline-none focus:border-transparent focus:ring-indigo-600`}
                    type="text"
                    placeholder="john.doe@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                <label className="block mb-5 relative">
                  <p className="mb-2 text-neutral-900 font-semibold leading-normal">
                    Password
                  </p>
                  <input
                    className={`px-4 py-3.5 w-full text-neutral-700 font-normal placeholder-neutral-400 bg-white outline-none border ${
                      passwordError
                        ? "border-red-500 ring-red-500"
                        : "border-neutral-300"
                    } rounded-lg focus:ring-2 focus:outline-none focus:border-transparent focus:ring-indigo-600`}
                    type={showPassword ? "text" : "password"}
                    placeholder="•••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute w-8 h-8 rounded-full right-3 top-[42px] text-neutral-700 hover:bg-neutral-200/50 focus:outline-none"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? (
                      <i className="fa-solid fa-eye"></i>
                    ) : (
                      <i className="fa-solid fa-eye-slash"></i>
                    )}
                  </button>
                </label>
                <div className="flex flex-row items-center justify-center w-full mb-6">
                  <label
                    className="ml-0.5 text-xs text-neutral-500 font-light"
                    htmlFor="default-checkbox"
                  >
                    <span>By signing up, I agree to the </span>
                    <a
                      className="text-indigo-600 hover:text-indigo-700"
                      href="/terms"
                    >
                      Terms and Conditions
                    </a>
                  </label>
                </div>
                <button
                  className="py-3 px-6 w-full text-white font-semibold border border-indigo-700 rounded-lg shadow-4xl focus:ring-0 focus:border-transparent focus:outline-none bg-indigo-600 hover:bg-indigo-700 transition ease-in-out duration-200"
                  type="submit"
                >
                  {isLoading ? (
                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                  ) : isCheckingAccess ? (
                    "Checking Access..."
                  ) : (
                    "Get Started"
                  )}
                </button>
              </form>
              <p className="my-4 text-sm text-neutral-500 font-light text-center">
                Or
              </p>
              <div className="w-full flex flex-col justify-center items-center space-y-3.5 mb-8">
                <div className="w-full flex flex-row justify-center items-center space-x-4">
                  <button
                    onClick={handleGoogleSignIn}
                    className="w-full flex justify-center items-center py-3 bg-white ring-1 ring-neutral-200 hover:bg-neutral-100 hover:shadow-lg rounded-lg transition ease-in-out duration-300 gap-3"
                    disabled={isSocialLoading}
                  >
                    <FcGoogle className="w-6 h-6" />
                    <span className="text-sm font-normal text-neutral-700">
                      Continue with Google
                    </span>
                  </button>
                  {/* <button
                    onClick={handleFacebookSignIn}
                    className="flex justify-center items-center w-14 h-14 bg-[#316FF6] hover:bg-blue-700 ring-1 ring-[#316FF6] hover:shadow-lg rounded-lg transition ease-in-out duration-300"
                  >
                    <FaFacebook className="text-[28px] text-white" />
                  </button> */}
                </div>
                <p className="mt-4 text-neutral-500 text-[15px] font-light">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-indigo-600 hover:text-indigo-800"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignUp;
